import { Fragment, useEffect, useRef, useState } from 'react';

import KeyboardShortcut from '@snowit/components/Utils/KeyboardShortcut';
import { IS_MACOS, useHotkeys } from '@snowit/hooks/useHotkeys';
import { Modal } from '@snowit/hooks/useModals';
import useBookmarks from '@snowit/hookspannello/useBookmarks';
import useAuth from '@snowit/system/useAuth';
import useLanguage from '@snowit/system/useLanguage';
import { BadgeLabel, ButtonIcon, Input, Typo } from '@snowit/ui';
import Icon from '@snowit/ui/Icon';
import { useRouter } from '@snowit/wouter';

import cx from 'classnames';
import s from './QuickJump.module.scss';

type QuickJumpEntry = {
  path: string;
  label: string;
  group: string;
};

const SHORTCUT = IS_MACOS ? 'meta+k' : 'ctrl+k';

export type QuickJumpProps = {};

const QuickJump = () => {
  const [mounted, setMounted] = useState(false);
  const [input, setInput] = useState<string>('');
  const { authType } = useAuth();
  const { t } = useLanguage();
  const [focused, setFocused] = useState<number>(-1);
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const ref = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingNavigation = useRef<string | null>(null);
  const { pushHistory } = useRouter();
  const { routes, bookmarks, toggleBookmark } = useBookmarks();

  useEffect(() => {
    if (mounted && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mounted]);

  const list: QuickJumpEntry[] = routes
    .filter((entry) => {
      if (!entry.label) return false;
      if (!input) return true;
      return (
        entry.label.toLowerCase().includes(input.toLowerCase()) ||
        entry.path.toLowerCase().includes(input.toLowerCase())
      );
    })
    .sort((a, b) => {
      const byBookmarks = Number(bookmarks.has(b.path)) - Number(bookmarks.has(a.path));
      const byGroup = a.index - b.index;
      const byLabel = a.label.localeCompare(b.label);
      const score = byBookmarks * 100 + byGroup * 10 + byLabel;
      return score;
    });

  const handleSearch = (value: string) => {
    setInput(value);
    setFocused(-1);
    if (ref.current) {
      ref.current.scrollTo({ top: 0 });
    }
  };

  const handleClose = () => {
    setMounted(false);
    setInput('');
    setFocused(-1);
  };

  useEffect(() => {
    if (mounted || !pendingNavigation.current) return;

    const path = pendingNavigation.current;
    pendingNavigation.current = null;
    pushHistory(path);
  }, [mounted, pushHistory]);

  const handleClick = (index: number) => {
    const entry = list[index];
    if (!entry) return;
    handleClose();
    pendingNavigation.current = entry.path;
  };

  useEffect(() => {
    if (!refs.current?.[focused]) return () => {};
    refs.current[focused].scrollIntoView();
  }, [refs, focused]);

  useHotkeys({
    keys: SHORTCUT,
    action: () => setMounted((p) => !p),
    enabled: authType === 'ADMIN',
  });

  useHotkeys({
    keys: 'esc',
    action: () => handleClose(),
    enabled: mounted,
  });

  useHotkeys({
    keys: 'down',
    action: () => setFocused((p) => Math.min(list.length - 1, p + 1)),
    dependencies: [list.length],
  });

  useHotkeys({
    keys: 'up',
    action: () => setFocused((p) => Math.max(0, p - 1)),
    dependencies: [list.length],
  });

  useHotkeys({
    keys: 'enter',
    action: () => handleClick(focused),
    enabled: mounted,
    dependencies: [focused],
  });

  const renderButton = () => {
    return (
      <div className={s.fakeInput} onClick={() => setMounted(true)}>
        <Icon name={'search'} sizeV2={[1]} />
        <Typo className={s.labelFakeInput} text={t('lzy.label.quick_jump')} />
        <KeyboardShortcut shortcut={SHORTCUT} />
      </div>
    );
  };

  const renderBookmark = (path: string) => {
    return (
      <ButtonIcon
        icon={bookmarks.has(path) ? 'bookmark_fill' : 'bookmark'}
        onClick={({ event }) => {
          event.stopPropagation();
          toggleBookmark(path);
        }}
        theme={'transparent'}
        size={'db'}
        color={bookmarks.has(path) ? 'primary' : 'text'}
      />
    );
  };

  const renderList = () => {
    return (
      <div className={s.list} ref={ref}>
        {list.map((entry, index) => (
          <div
            key={entry.path}
            ref={(el) => {
              refs.current[index] = el;
            }}
            onClick={() => handleClick(index)}
            className={cx(s.entry, { [s.active]: focused === index })}
          >
            <Typo className={s.label} text={entry.label} />
            <BadgeLabel label={entry.group} />
            {renderBookmark(entry.path)}
          </div>
        ))}
      </div>
    );
  };

  const renderBody = () => {
    return (
      <Fragment>
        <Input
          ref={inputRef}
          icon={'search'}
          value={input}
          placeholder={'search'}
          onChange={({ value }) => handleSearch(value)}
        />
        {renderList()}
      </Fragment>
    );
  };

  return (
    <Fragment>
      {renderButton()}
      <Modal
        id={'quick_jump'}
        classNames={{ modal: s.modal, body: s.bodyModal }}
        header={t('lzy.label.quick_jump')}
        body={renderBody}
        size={'lg'}
        opened={mounted}
        onClose={handleClose}
      />
    </Fragment>
  );
};

export default QuickJump;
